import { useMemo, useState, type ReactElement } from 'react';
import type { WorkoutStreamsResponse, WorkoutStreams, Discipline } from '@/lib/workouts';

/**
 * Gráfico SVG inline de streams Strava (M2.15 / M2.17).
 *
 * 3 líneas en el mismo eje X (tiempo en minutos):
 *   - HR (rose)
 *   - Power (orange)
 *   - Velocidad/Pace (cyan) — unidad según `discipline`
 * + Altitude como área gris detrás (eje Y secundario implícito).
 *
 * Toggles arriba para mostrar/ocultar cada serie. Tooltip en hover.
 *
 * Unidades por disciplina (M2.17):
 *   - bike + strength/brick/other: km/h (mayor = arriba).
 *   - run: pace min/km (eje invertido — menor pace = arriba).
 *   - swim: pace min/100m (eje invertido).
 *
 * Si todos los valores de la serie velocidad son null (por ejemplo, run en
 * cinta sin GPS), la serie no se renderiza (ni línea ni toggle).
 *
 * Decimación: si timeS tiene >300 puntos, samplea cada `floor(len/300)` para
 * mantener el SVG ligero.
 */

const WIDTH = 720;
const HEIGHT = 240;
const PADDING = { top: 8, right: 24, bottom: 24, left: 36 };
const PLOT_W = WIDTH - PADDING.left - PADDING.right;
const PLOT_H = HEIGHT - PADDING.top - PADDING.bottom;

const MAX_POINTS = 300;

type SeriesKey = 'hr' | 'watts' | 'velocity' | 'altitude';

interface SeriesMeta {
  key: SeriesKey;
  label: string;
  color: string;
}

const STATIC_SERIES_META: Record<Exclude<SeriesKey, 'velocity'>, SeriesMeta> = {
  hr: { key: 'hr', label: 'FC', color: '#e11d48' },          // rose
  watts: { key: 'watts', label: 'Potencia', color: '#ea580c' }, // orange
  altitude: { key: 'altitude', label: 'Altitud', color: '#94a3b8' }, // slate
};

const VELOCITY_COLOR = '#0891b2'; // cyan

export interface StreamsChartProps {
  data: WorkoutStreamsResponse;
  /** Disciplina del actual; controla la unidad de la serie velocidad. */
  discipline?: Discipline;
}

export function StreamsChart({ data, discipline = 'bike' }: StreamsChartProps): ReactElement {
  if (data.streams === null || data.streams.timeS.length === 0) {
    return (
      <div
        data-testid="streams-empty"
        className="rounded-md bg-slate-50 px-3 py-4 text-sm text-slate-600"
      >
        Sin streams disponibles.
        {data.reason ? (
          <>
            {' '}
            <span className="text-slate-500">Razón: {humanizeReason(data.reason)}.</span>
          </>
        ) : null}
      </div>
    );
  }

  return <StreamsChartInner streams={data.streams} discipline={discipline} />;
}

interface SampledData {
  /** Tiempo en MINUTOS (display). */
  tMin: number[];
  hr: Array<number | null>;
  watts: Array<number | null>;
  /** Valor en la unidad de display (km/h o min/km o min/100m). */
  velocityDisplay: Array<number | null>;
  altitude: Array<number | null>;
}

type VelocityMode = 'speed' | 'pace';

interface VelocityConfig {
  mode: VelocityMode;
  /** Etiqueta para leyenda y tooltip (corta). */
  label: string;
  /** Unidad para mostrar en tooltip. */
  unit: string;
  /** Si true, el eje está invertido: valor menor → más arriba. */
  inverted: boolean;
  /** Convierte m/s al valor display, devuelve null si no procede. */
  fromMps: (mps: number) => number | null;
  /** Formatea el valor display para el tooltip (sin unidad). */
  formatValue: (v: number) => string;
}

function buildVelocityConfig(discipline: Discipline): VelocityConfig {
  switch (discipline) {
    case 'run':
      return {
        mode: 'pace',
        label: 'Pace',
        unit: 'min/km',
        inverted: true,
        fromMps: (mps) => (mps > 0.5 ? 1000 / mps / 60 : null),
        formatValue: formatPaceMmss,
      };
    case 'swim':
      return {
        mode: 'pace',
        label: 'Pace',
        unit: 'min/100m',
        inverted: true,
        fromMps: (mps) => (mps > 0.1 ? 100 / mps / 60 : null),
        formatValue: formatPaceMmss,
      };
    case 'bike':
    case 'strength':
    case 'brick':
    case 'other':
    default:
      return {
        mode: 'speed',
        label: 'Velocidad',
        unit: 'km/h',
        inverted: false,
        fromMps: (mps) => Math.round(mps * 3.6 * 10) / 10,
        formatValue: (v) => v.toFixed(1),
      };
  }
}

function StreamsChartInner({
  streams,
  discipline,
}: {
  streams: WorkoutStreams;
  discipline: Discipline;
}): ReactElement {
  const velocityCfg = useMemo(() => buildVelocityConfig(discipline), [discipline]);

  const sampled = useMemo<SampledData>(
    () => decimate(streams, velocityCfg),
    [streams, velocityCfg],
  );

  // Si todos los valores de velocidad son null (e.g. run sin GPS), no
  // mostramos serie ni toggle.
  const hasVelocitySeries = useMemo(
    () => sampled.velocityDisplay.some((v) => typeof v === 'number' && Number.isFinite(v)),
    [sampled.velocityDisplay],
  );

  const [enabled, setEnabled] = useState<Record<SeriesKey, boolean>>({
    hr: true,
    watts: true,
    velocity: true,
    altitude: true,
  });
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // Calcular escalas Y por serie. Para mantener las 3 líneas comparables en
  // escala visual, normalizamos al [0, 1] del plot por separado.
  const scales = useMemo(() => computeScales(sampled), [sampled]);

  // X scale: índice → píxel.
  const n = sampled.tMin.length;
  function xPx(i: number): number {
    if (n <= 1) return PADDING.left;
    return PADDING.left + (i / (n - 1)) * PLOT_W;
  }
  function yPxStd(norm: number): number {
    // norm en [0,1]; 0 abajo, 1 arriba.
    return PADDING.top + (1 - norm) * PLOT_H;
  }
  function yPxInverted(norm: number): number {
    // norm en [0,1]; 0 arriba, 1 abajo. Para pace queremos que menor valor
    // (más rápido) aparezca arriba. `normalize` ya da (val-min)/(max-min);
    // con `inverted` queremos un valor BAJO en arriba → mismo y que std
    // PERO calculado con la fórmula inversa equivalente a usar (1 - norm)
    // como base. La diferencia respecto a yPxStd es que esta función espera
    // un norm "ya invertido" upstream — para minimizar confusión, dejamos la
    // serie velocidad usar `yPxStd` con un `normalize` invertido externo.
    return PADDING.top + norm * PLOT_H;
  }

  // Generar paths.
  const altitudeAreaPath = useMemo(
    () => buildAreaPath(sampled.altitude, scales.altitude, xPx, yPxStd),
    [sampled.altitude, scales.altitude],
  );
  const hrPath = useMemo(
    () => buildLinePath(sampled.hr, scales.hr, xPx, yPxStd),
    [sampled.hr, scales.hr],
  );
  const wattsPath = useMemo(
    () => buildLinePath(sampled.watts, scales.watts, xPx, yPxStd),
    [sampled.watts, scales.watts],
  );
  const velPath = useMemo(() => {
    if (!hasVelocitySeries) return null;
    if (velocityCfg.inverted) {
      // Si el eje está invertido (pace): menor valor → arriba. Forzamos esto
      // usando `yPxInverted` con la normalización estándar.
      return buildLinePath(sampled.velocityDisplay, scales.velocity, xPx, yPxInverted);
    }
    return buildLinePath(sampled.velocityDisplay, scales.velocity, xPx, yPxStd);
  }, [sampled.velocityDisplay, scales.velocity, velocityCfg.inverted, hasVelocitySeries]);

  // X ticks: ~6 marks distribuidos.
  const xTicks = useMemo(() => buildXTicks(sampled.tMin), [sampled.tMin]);

  const tooltipIdx = hoverIdx;

  function onMove(e: React.MouseEvent<SVGSVGElement>): void {
    const rect = e.currentTarget.getBoundingClientRect();
    const xRel = ((e.clientX - rect.left) / rect.width) * WIDTH;
    if (xRel < PADDING.left || xRel > WIDTH - PADDING.right) {
      setHoverIdx(null);
      return;
    }
    const ratio = (xRel - PADDING.left) / PLOT_W;
    const idx = Math.round(ratio * (n - 1));
    setHoverIdx(Math.max(0, Math.min(n - 1, idx)));
  }

  function onLeave(): void {
    setHoverIdx(null);
  }

  // Construir lista de series visibles (dinámico — la velocidad puede no estar).
  const seriesList: SeriesMeta[] = [
    STATIC_SERIES_META.hr,
    STATIC_SERIES_META.watts,
    ...(hasVelocitySeries
      ? [{ key: 'velocity' as SeriesKey, label: velocityCfg.label, color: VELOCITY_COLOR }]
      : []),
    STATIC_SERIES_META.altitude,
  ];

  return (
    <div className="space-y-3">
      <fieldset className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-700">
        <legend className="sr-only">Series a mostrar</legend>
        {seriesList.map((s) => (
          <label key={s.key} className="inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              data-testid={`streams-toggle-${s.key}`}
              checked={enabled[s.key]}
              onChange={(e) =>
                setEnabled((prev) => ({ ...prev, [s.key]: e.target.checked }))
              }
              className="h-3.5 w-3.5 rounded border-slate-300 accent-slate-700"
            />
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ background: s.color }}
            />
            <span>
              {s.label}
              {s.key === 'velocity' ? (
                <span className="ml-1 text-slate-500">({velocityCfg.unit})</span>
              ) : null}
            </span>
          </label>
        ))}
      </fieldset>

      <div className="overflow-x-auto">
        <svg
          role="img"
          aria-label={`Gráfico de streams del entrenamiento (velocidad en ${velocityCfg.unit})`}
          data-testid="streams-svg"
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          width="100%"
          className="block max-w-full"
          onMouseMove={onMove}
          onMouseLeave={onLeave}
        >
          {/* Ejes simples */}
          <line
            x1={PADDING.left}
            y1={HEIGHT - PADDING.bottom}
            x2={WIDTH - PADDING.right}
            y2={HEIGHT - PADDING.bottom}
            stroke="#cbd5e1"
            strokeWidth={1}
          />
          {/* X ticks */}
          {xTicks.map((t) => (
            <g key={`tick-${t.idx}`}>
              <line
                x1={xPx(t.idx)}
                y1={HEIGHT - PADDING.bottom}
                x2={xPx(t.idx)}
                y2={HEIGHT - PADDING.bottom + 4}
                stroke="#cbd5e1"
                strokeWidth={1}
              />
              <text
                x={xPx(t.idx)}
                y={HEIGHT - 6}
                textAnchor="middle"
                fontSize={10}
                fill="#64748b"
              >
                {t.label}
              </text>
            </g>
          ))}

          {/* Altitude area (detrás) */}
          {enabled.altitude && altitudeAreaPath && (
            <path
              d={altitudeAreaPath}
              fill="#94a3b8"
              fillOpacity={0.18}
              stroke="none"
              data-testid="streams-line-altitude"
            />
          )}

          {/* Líneas */}
          {enabled.hr && hrPath && (
            <path
              d={hrPath}
              fill="none"
              stroke="#e11d48"
              strokeWidth={1.5}
              data-testid="streams-line-hr"
            />
          )}
          {enabled.watts && wattsPath && (
            <path
              d={wattsPath}
              fill="none"
              stroke="#ea580c"
              strokeWidth={1.5}
              data-testid="streams-line-watts"
            />
          )}
          {enabled.velocity && velPath && hasVelocitySeries && (
            <path
              d={velPath}
              fill="none"
              stroke={VELOCITY_COLOR}
              strokeWidth={1.5}
              data-testid="streams-line-velocity"
              data-velocity-mode={velocityCfg.mode}
              data-velocity-unit={velocityCfg.unit}
            />
          )}

          {/* Cursor + tooltip */}
          {tooltipIdx !== null && (
            <g>
              <line
                x1={xPx(tooltipIdx)}
                y1={PADDING.top}
                x2={xPx(tooltipIdx)}
                y2={HEIGHT - PADDING.bottom}
                stroke="#334155"
                strokeWidth={1}
                strokeDasharray="2 2"
                opacity={0.7}
              />
            </g>
          )}
        </svg>
      </div>

      {tooltipIdx !== null && (
        <div
          role="status"
          aria-live="polite"
          data-testid="streams-tooltip"
          className="rounded-md bg-slate-900 px-3 py-2 text-xs text-white shadow"
        >
          <div className="font-semibold">
            t = {formatMinutes(sampled.tMin[tooltipIdx] ?? 0)}
          </div>
          <ul className="mt-1 space-y-0.5">
            {enabled.hr && (
              <li>
                FC: {formatNullable(sampled.hr[tooltipIdx])} bpm
              </li>
            )}
            {enabled.watts && (
              <li>
                Potencia: {formatNullable(sampled.watts[tooltipIdx])} W
              </li>
            )}
            {enabled.velocity && hasVelocitySeries && (
              <li>
                {velocityCfg.label}: {formatVelocityValue(sampled.velocityDisplay[tooltipIdx], velocityCfg)}
              </li>
            )}
            {enabled.altitude && (
              <li>
                Altitud: {formatNullable(sampled.altitude[tooltipIdx])} m
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function decimate(streams: WorkoutStreams, velocityCfg: VelocityConfig): SampledData {
  const n = streams.timeS.length;
  const step = n > MAX_POINTS ? Math.floor(n / MAX_POINTS) : 1;

  const tMin: number[] = [];
  const hr: Array<number | null> = [];
  const watts: Array<number | null> = [];
  const velocityDisplay: Array<number | null> = [];
  const altitude: Array<number | null> = [];

  for (let i = 0; i < n; i += step) {
    const t = streams.timeS[i];
    if (typeof t !== 'number') continue;
    tMin.push(t / 60);
    hr.push(streams.heartrateBpm?.[i] ?? null);
    watts.push(streams.watts?.[i] ?? null);
    const v = streams.velocityMps?.[i] ?? null;
    velocityDisplay.push(
      typeof v === 'number' && Number.isFinite(v) ? velocityCfg.fromMps(v) : null,
    );
    altitude.push(streams.altitudeM?.[i] ?? null);
  }

  return { tMin, hr, watts, velocityDisplay, altitude };
}

interface Scale {
  min: number;
  max: number;
}

function computeScales(s: SampledData): Record<SeriesKey, Scale> {
  return {
    hr: rangeOrFallback(s.hr, 80, 180),
    watts: rangeOrFallback(s.watts, 0, 300),
    velocity: rangeOrFallback(s.velocityDisplay, 0, 30),
    altitude: rangeOrFallback(s.altitude, 0, 100),
  };
}

function rangeOrFallback(arr: Array<number | null>, fbMin: number, fbMax: number): Scale {
  let min = Infinity;
  let max = -Infinity;
  for (const v of arr) {
    if (typeof v === 'number' && Number.isFinite(v)) {
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  if (min === Infinity || max === -Infinity) return { min: fbMin, max: fbMax };
  if (min === max) {
    // Evita dividir entre 0 más adelante.
    return { min: min - 1, max: max + 1 };
  }
  return { min, max };
}

function normalize(v: number, scale: Scale): number {
  return (v - scale.min) / (scale.max - scale.min);
}

function buildLinePath(
  values: Array<number | null>,
  scale: Scale,
  xPx: (i: number) => number,
  yPx: (norm: number) => number,
): string | null {
  const parts: string[] = [];
  let isFirst = true;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      // Gap en la línea: cierra el path actual y reabre con M en el próximo.
      isFirst = true;
      continue;
    }
    const x = xPx(i).toFixed(1);
    const y = yPx(normalize(v, scale)).toFixed(1);
    parts.push(`${isFirst ? 'M' : 'L'}${x},${y}`);
    isFirst = false;
  }
  return parts.length > 0 ? parts.join(' ') : null;
}

function buildAreaPath(
  values: Array<number | null>,
  scale: Scale,
  xPx: (i: number) => number,
  yPx: (norm: number) => number,
): string | null {
  // Para que sea área, cerramos contra la base inferior. Si hay null,
  // partimos en sub-áreas.
  const segments: Array<Array<{ x: number; y: number }>> = [];
  let current: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      if (current.length > 0) {
        segments.push(current);
        current = [];
      }
      continue;
    }
    current.push({ x: xPx(i), y: yPx(normalize(v, scale)) });
  }
  if (current.length > 0) segments.push(current);
  if (segments.length === 0) return null;

  const baseY = PADDING.top + PLOT_H;
  const paths: string[] = [];
  for (const seg of segments) {
    if (seg.length === 0) continue;
    const first = seg[0];
    const last = seg[seg.length - 1];
    if (!first || !last) continue;
    const parts: string[] = [];
    parts.push(`M${first.x.toFixed(1)},${baseY.toFixed(1)}`);
    for (const p of seg) {
      parts.push(`L${p.x.toFixed(1)},${p.y.toFixed(1)}`);
    }
    parts.push(`L${last.x.toFixed(1)},${baseY.toFixed(1)}`);
    parts.push('Z');
    paths.push(parts.join(' '));
  }
  return paths.length > 0 ? paths.join(' ') : null;
}

function buildXTicks(tMin: number[]): Array<{ idx: number; label: string }> {
  if (tMin.length === 0) return [];
  const n = tMin.length;
  const count = Math.min(6, n);
  const ticks: Array<{ idx: number; label: string }> = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.round((i / (count - 1 || 1)) * (n - 1));
    const v = tMin[idx];
    if (typeof v !== 'number') continue;
    ticks.push({ idx, label: formatMinutes(v) });
  }
  return ticks;
}

function formatMinutes(m: number): string {
  if (m < 60) return `${Math.round(m)}min`;
  const h = Math.floor(m / 60);
  const min = Math.round(m - h * 60);
  return min > 0 ? `${h}h${min}` : `${h}h`;
}

function formatNullable(v: number | null | undefined): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  return String(Math.round(v));
}

function formatVelocityValue(
  v: number | null | undefined,
  cfg: VelocityConfig,
): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  return `${cfg.formatValue(v)} ${cfg.unit}`;
}

/**
 * Convierte un valor de pace en minutos decimales (ej 4.583) a "MM:SS"
 * (ej "4:35"). Si el valor es entero, segundos = 0.
 */
export function formatPaceMmss(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '—';
  const m = Math.floor(value);
  const s = Math.round((value - m) * 60);
  // Si los segundos redondean a 60, sube el minuto.
  if (s === 60) return `${m + 1}:00`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function humanizeReason(reason: string): string {
  switch (reason) {
    case 'manual_or_no_strava':
      return 'la actividad no es de Strava';
    case 'no_strava_creds':
      return 'Strava no está conectado';
    case 'strava_404':
      return 'la actividad ya no existe en Strava';
    default:
      return reason;
  }
}
